/**
 * Rendered body heading and locomotion gait (pure, no three.js), after WoW's display facing
 * (benilla player/gait.rs, creature_anim/select.rs + twist.rs):
 *
 *  - The body never pops to a new facing. Moving, it eases toward the aim at ~17/s in
 *    aim-relative space (a 180° flip swings round smoothly). Strafing it turns ±90° off the aim
 *    (±45° on diagonals, mirrored while backpedalling) and the spine / head counter-twist back
 *    toward the aim, half the gap each, capped at 45°.
 *  - Standing while steering (mouselook), the body holds while the aim is within 90° and only
 *    follows past that ceiling, then sweeps onto the aim; otherwise it sweeps onto any new facing
 *    fast (180° in ~0.2 s). Either way with a shuffle-step pose.
 *  - The gait (run / backpedal / side-step) comes from the velocity relative to the rendered body,
 *    blended with ~150 ms smoothsteps; the cycle rate is speed / stride, so feet don't slide.
 */
import type { Vec2 } from '@keystone/sim';

/** Moving body ease toward its target heading (1/s; benilla's 17.26). */
export const BODY_EASE_RATE = 17.26;
/** Standing: the body holds up to this far off the aim. */
export const STAND_CEILING = Math.PI / 2;
/** Standing sweep onto the aim (rad/s). */
export const STAND_SWEEP_RATE = 5 * Math.PI;
/** Below this ground speed (yd/s) a unit counts as standing. */
export const MOVE_EPS = 0.6;
/** Gait blend time (s) between run / backpedal / side-step. */
export const GAIT_BLEND_S = 0.15;
/** One full run cycle (two steps) per this many body heights at full stride. */
export const STRIDE_PER_HEIGHT = 1.78;
/**
 * Backpedal steps are a little shorter than the run stride; at backpedal speed (4.5/7) the cycle
 * then runs ~0.8× the run cadence (reversed, visibly slower). Side steps are shorter (quicker).
 */
export const BACK_STRIDE = 0.8;
const SIDE_STRIDE = 0.72;
/** One shuffle step per this much body turn (rad). */
const SHUFFLE_STEP_RAD = Math.PI / 4;
/** sin(22.5°): lateral share above which a move counts as a strafe. */
const STRAFE_SIN = Math.sin(Math.PI / 8);
const TWIST_CAP = Math.PI / 4;

export const wrapPi = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
const smoothstep = (x: number): number => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

/**
 * Body offset from the aim while strafing (benilla strafe_body_offset): ±π/2 pure strafe, ±π/4
 * on diagonals, left positive, mirrored while backpedalling; 0 when not strafing.
 * `fwd` / `side`: the move direction in aim space (cos / sin of its angle off the aim, side > 0 = left).
 */
export function strafeOffset(fwd: number, side: number): number {
  if (Math.abs(side) <= STRAFE_SIN) return 0;
  const mag = Math.abs(fwd) > STRAFE_SIN ? Math.PI / 4 : Math.PI / 2;
  return side > 0 !== fwd < -STRAFE_SIN ? mag : -mag;
}

/** Spine / head counter-twist toward the aim (benilla twist_shares): half the gap each, capped at 45°. */
export function twistShares(gap: number): [spine: number, head: number] {
  const spine = Math.max(-TWIST_CAP, Math.min(TWIST_CAP, gap * 0.5));
  const head = Math.max(-TWIST_CAP, Math.min(TWIST_CAP, gap - spine));
  return [spine, head];
}

/** Ground covered by one full cycle (two steps) for a unit of `height` with gait weights `back` / `side`. */
export function strideLength(height: number, back = 0, side = 0): number {
  const k = 1 - (1 - BACK_STRIDE) * back - (1 - SIDE_STRIDE) * side;
  return Math.max(0.3, height * STRIDE_PER_HEIGHT * k);
}

/** Run cycles per second at `speed` yd/s (speed / stride: no foot sliding). */
export function cadenceHz(speed: number, height: number, back = 0, side = 0): number {
  return Math.max(0, speed) / strideLength(height, back, side);
}

export interface BodyInput {
  /** Aim: the unit's facing (radians, sim convention). */
  aim: number;
  /** Ground velocity (yd/s); absent = standing. */
  vel?: Vec2 | undefined;
  /** Mouselook: a standing body holds within the 90° ceiling instead of sweeping. */
  steering?: boolean | undefined;
  alive: boolean;
}

/** Rendered body heading for one unit. */
export class BodyYaw {
  /** Rendered heading (radians, sim convention). NaN until the first update. */
  yaw = Number.NaN;
  /** Signed body turn applied this frame while standing (drives the shuffle). */
  turnStep = 0;
  private sweeping = false;

  /** Snap to the aim (teleports, respawns). */
  reset(aim: number): void {
    this.yaw = aim;
    this.turnStep = 0;
    this.sweeping = false;
  }

  /** wrap(aim − rendered yaw): what the spine / head twist closes. */
  gap(aim: number): number {
    return Number.isNaN(this.yaw) ? 0 : wrapPi(aim - this.yaw);
  }

  update(i: BodyInput, dt: number): number {
    this.turnStep = 0;
    if (Number.isNaN(this.yaw) || !i.alive) {
      this.reset(i.aim);
      return this.yaw;
    }
    const vx = i.vel?.x ?? 0;
    const vy = i.vel?.y ?? 0;
    const speed = Math.hypot(vx, vy);
    if (speed > MOVE_EPS) {
      this.sweeping = false;
      const rel = Math.atan2(vy, vx) - i.aim;
      const offset = strafeOffset(Math.cos(rel), Math.sin(rel));
      const cur = wrapPi(this.yaw - i.aim);
      const eased = cur + (offset - cur) * (1 - Math.exp(-BODY_EASE_RATE * Math.max(0, dt)));
      this.yaw = wrapPi(i.aim + eased);
      return this.yaw;
    }
    const delta = wrapPi(i.aim - this.yaw);
    const abs = Math.abs(delta);
    if (abs > STAND_CEILING) this.sweeping = true;
    if (!i.steering && abs > 1e-3) this.sweeping = true;
    // Steering, the aim moves continuously: the 90° ceiling drags the body along with it. Other
    // jumps (auto-face, a cast's facing) are swept, never popped.
    let step = i.steering ? Math.max(0, abs - STAND_CEILING) : 0;
    if (this.sweeping) step += STAND_SWEEP_RATE * Math.max(0, dt);
    step = Math.min(step, abs);
    if (abs - step < 1e-3) this.sweeping = false;
    this.turnStep = Math.sign(delta) * step;
    this.yaw = wrapPi(this.yaw + this.turnStep);
    return this.yaw;
  }
}

/** Gait weights (0..1 each, summing to 1 while moving) relative to the rendered body. */
export interface GaitWeights {
  fwd: number;
  back: number;
  side: number;
  /** +1 stepping left, -1 right (meaningful while side > 0). */
  sideSign: number;
}

/** Target gait for velocity `vel` against body heading `yaw` (all zero when standing). */
export function gaitTarget(vel: Vec2 | undefined, yaw: number): GaitWeights {
  const vx = vel?.x ?? 0;
  const vy = vel?.y ?? 0;
  if (Math.hypot(vx, vy) <= MOVE_EPS) return { fwd: 0, back: 0, side: 0, sideSign: 1 };
  const rel = Math.atan2(vy, vx) - yaw;
  const c = Math.cos(rel);
  const s = Math.sin(rel);
  return { fwd: c > 0 ? c * c : 0, back: c < 0 ? c * c : 0, side: s * s, sideSign: s >= 0 ? 1 : -1 };
}

/** Smoothstep-blended gait weights: each ramps linearly over GAIT_BLEND_S, output smoothstepped and renormalized. */
export class GaitBlend {
  private readonly raw = { fwd: 0, back: 0, side: 0 };
  readonly out: GaitWeights = { fwd: 0, back: 0, side: 0, sideSign: 1 };

  update(target: GaitWeights, dt: number): GaitWeights {
    const k = Math.max(0, dt) / GAIT_BLEND_S;
    const r = this.raw;
    const total = target.fwd + target.back + target.side;
    // Standing keeps the last gait (the run blend fades the pose out), so stopping doesn't flip it.
    if (total > 0) {
      r.fwd += Math.max(-k, Math.min(k, target.fwd - r.fwd));
      r.back += Math.max(-k, Math.min(k, target.back - r.back));
      r.side += Math.max(-k, Math.min(k, target.side - r.side));
      if (target.side > 0.05) this.out.sideSign = target.sideSign;
    }
    const f = smoothstep(r.fwd);
    const b = smoothstep(r.back);
    const s = smoothstep(r.side);
    const sum = f + b + s;
    if (sum > 1e-6) {
      this.out.fwd = f / sum;
      this.out.back = b / sum;
      this.out.side = s / sum;
    } else {
      this.out.fwd = 1;
      this.out.back = 0;
      this.out.side = 0;
    }
    return this.out;
  }
}

/** Shuffle-step phase advance for a standing body turn of `turnStep` radians. */
export const shuffleAdvance = (turnStep: number): number => (Math.abs(turnStep) / SHUFFLE_STEP_RAD) * Math.PI;

/**
 * Foot plants crossed going from run phase `a` to `b` (either direction): the left heel lands at
 * π/2, the right at 3π/2 (mod 2π). Returns 0 (none), 1 (left) or 2 (right): the last one crossed.
 */
export function footPlant(a: number, b: number): 0 | 1 | 2 {
  const ka = Math.floor((a - Math.PI / 2) / Math.PI);
  const kb = Math.floor((b - Math.PI / 2) / Math.PI);
  if (ka === kb) return 0;
  // Moving forward the plant crossed is kb's boundary; backward, ka's.
  const k = kb > ka ? kb : ka;
  return ((k % 2) + 2) % 2 === 0 ? 1 : 2;
}
